/**
 * Requires every signed-in user to accept the current Terms of Service and
 * Acceptable Use Policy before using the platform; acceptance is recorded server-side.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { acceptLegal, getLegalAcceptance, getLegalDocuments } from '@/api/legal';
import { useAuth } from '@/hooks/useAuth';
import LegalMarkdown from '@/components/legal/LegalMarkdown';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { DraftNotice } from '@/pages/LegalPage';
import { apiError } from '@/utils/apiError';

export default function LegalAcceptanceGate({ children }: { children: ReactNode }) {
  const { logout } = useAuth();
  const queryClient = useQueryClient();
  const [agreed, setAgreed] = useState(false);
  const acceptance = useQuery({ queryKey: ['legal-acceptance'], queryFn: getLegalAcceptance });
  const documents = useQuery({ queryKey: ['legal-documents'], queryFn: getLegalDocuments, enabled: acceptance.data?.accepted === false });
  const accept = useMutation({
    mutationFn: () => acceptLegal(documents.data!.version, documents.data!.sha256),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['legal-acceptance'] }),
    onError: () => { setAgreed(false); void queryClient.invalidateQueries({ queryKey: ['legal-documents'] }); },
  });

  if (acceptance.isLoading) return <LoadingSpinner label="Loading..." size="lg" />;
  if (acceptance.data?.accepted) return <>{children}</>;
  if (acceptance.error) return <p role="alert" className="m-6 rounded bg-red-50 p-3 text-red-700">{apiError(acceptance.error)}</p>;

  return (
    <main className="mx-auto max-w-3xl space-y-4 px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900">Review and accept</h1>
      <p className="text-sm text-gray-700">
        Before using Vantage by Velosynt, read the Terms of Service, the Acceptable Use Policy, and the Privacy Policy. By accepting, you agree to be bound by the Terms of Service and the Acceptable Use Policy.
      </p>
      {documents.isLoading && <LoadingSpinner label="Loading..." />}
      {documents.data && (
        <>
          {documents.data.draft ? <DraftNotice version={documents.data.version} /> : <p className="text-sm text-gray-500">Version {documents.data.version}</p>}
          <div className="max-h-[50vh] space-y-6 overflow-y-auto rounded border border-gray-200 p-4">
            {documents.data.documents.map((doc) => (
              <section key={doc.id} aria-labelledby={`legal-${doc.id}`}>
                <h2 id={`legal-${doc.id}`} className="text-xl font-bold text-gray-900">{doc.title}</h2>
                <LegalMarkdown markdown={doc.markdown} />
              </section>
            ))}
          </div>
          {accept.error && <p role="alert" className="rounded bg-red-50 p-3 text-red-700">{apiError(accept.error)}</p>}
          <label className="flex items-start gap-2 text-sm text-gray-800">
            <input type="checkbox" className="mt-1" checked={agreed} onChange={(event) => setAgreed(event.target.checked)} />
            <span>I have read and agree to the Terms of Service and the Acceptable Use Policy, and I agree to be bound by them, and I acknowledge the Privacy Policy.</span>
          </label>
          <div className="flex flex-wrap gap-3">
            <button className="btn-primary" disabled={!agreed || accept.isPending} onClick={() => accept.mutate()}>Accept and continue</button>
            <button className="btn-secondary" onClick={() => logout()}>Decline and sign out</button>
          </div>
        </>
      )}
      {documents.error && <p role="alert" className="rounded bg-red-50 p-3 text-red-700">{apiError(documents.error)}</p>}
    </main>
  );
}
